'use client'

import { Play } from 'lucide-react'
import Image from 'next/image'
import React, { useEffect, useRef, useState } from 'react'

import { cn } from '@/utilities/ui'

export type PolaroidCardProps = {
  autoplayVideo?: boolean
  caption?: string
  captionAlign?: 'left' | 'center' | 'right'
  captionLine1?: string
  captionLine2?: string
  className?: string
  image: string
  imageAlt: string
  isVideoPlaying?: boolean
  onOpenDetail?: () => void
  onPlayClick?: () => void
  onVideoEnded?: () => void
  openDetailAriaLabel?: string
  playAriaLabel?: string
  playButtonRef?: React.Ref<HTMLButtonElement>
  playLabel: string
  triggerRef?: React.Ref<HTMLButtonElement>
  videoMimeType?: string
  videoUrl?: string
}

const captionAlignClasses = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
} as const

const AUTOPLAY_IN_VIEW_RATIO = 0.35

export function PolaroidCard({
  autoplayVideo = false,
  caption,
  captionAlign = 'left',
  captionLine1,
  captionLine2,
  className,
  image,
  imageAlt,
  isVideoPlaying = false,
  onOpenDetail,
  onPlayClick,
  onVideoEnded,
  openDetailAriaLabel,
  playAriaLabel,
  playButtonRef,
  playLabel,
  triggerRef,
  videoMimeType,
  videoUrl,
}: PolaroidCardProps) {
  const articleRef = useRef<HTMLElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isInView, setIsInView] = useState(false)
  const [autoplayFailed, setAutoplayFailed] = useState(false)

  const hasVideo = Boolean(videoUrl && playAriaLabel && onPlayClick)
  const wantsMutedAutoplay = Boolean(autoplayVideo && hasVideo && !autoplayFailed)
  const showMutedAutoplay = wantsMutedAutoplay && isInView
  const showManualVideo = Boolean(isVideoPlaying && videoUrl && !showMutedAutoplay)
  const showPlayOverlay = hasVideo && !isVideoPlaying && !showMutedAutoplay
  const isInteractive = Boolean(onOpenDetail) && !isVideoPlaying && !showMutedAutoplay
  const hasCaption = Boolean(caption || captionLine1 || captionLine2)

  useEffect(() => {
    if (autoplayVideo) {
      return
    }

    setAutoplayFailed(false)
    setIsInView(false)
  }, [autoplayVideo])

  useEffect(() => {
    const node = articleRef.current
    if (!node || !wantsMutedAutoplay) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const inView = Boolean(
          entry?.isIntersecting && (entry.intersectionRatio ?? 0) >= AUTOPLAY_IN_VIEW_RATIO,
        )
        setIsInView(inView)
      },
      { threshold: [0, AUTOPLAY_IN_VIEW_RATIO, 0.6, 1] },
    )

    observer.observe(node)

    return () => {
      observer.disconnect()
    }
  }, [wantsMutedAutoplay])

  useEffect(() => {
    const video = videoRef.current
    if (!video || !showMutedAutoplay) {
      return
    }

    video.muted = true
    void video.play().catch(() => {
      setAutoplayFailed(true)
    })

    return () => {
      video.pause()
    }
  }, [showMutedAutoplay])

  useEffect(() => {
    const video = videoRef.current
    if (!isVideoPlaying || !video || showMutedAutoplay) return

    video.muted = true
    void video.play().catch(() => {
      // Autoplay can be blocked; native controls remain available.
    })
  }, [isVideoPlaying, showMutedAutoplay])

  const imageElement = (
    <Image
      alt={showMutedAutoplay || showManualVideo ? '' : imageAlt}
      aria-hidden={showMutedAutoplay || showManualVideo ? true : undefined}
      className="object-cover"
      fill
      sizes="312px"
      src={image}
    />
  )

  return (
    <article
      ref={articleRef}
      className={cn(
        'flex w-[19.5rem] max-w-[calc(100vw-2rem)] shrink-0 flex-col gap-3 rounded-xl border-[1.5px] border-black/10 bg-white p-3 transition-transform duration-300 motion-reduce:transform-none',
        className,
      )}
    >
      <div className="relative aspect-square overflow-hidden rounded-lg">
        {wantsMutedAutoplay && videoUrl ? (
          <>
            {imageElement}
            <video
              ref={videoRef}
              aria-label={playAriaLabel ?? imageAlt}
              className={cn(
                'pointer-events-none absolute inset-0 size-full object-cover',
                !showMutedAutoplay && 'invisible',
              )}
              data-gallery-interactive
              loop
              muted
              playsInline
              preload={showMutedAutoplay ? 'auto' : 'none'}
              tabIndex={-1}
            >
              <source src={videoUrl} type={videoMimeType ?? undefined} />
            </video>
          </>
        ) : showManualVideo ? (
          <>
            {imageElement}
            <video
              ref={videoRef}
              aria-label={playAriaLabel ?? imageAlt}
              className="absolute inset-0 size-full object-cover"
              controls
              data-gallery-interactive
              muted
              onEnded={onVideoEnded}
              playsInline
              preload="auto"
              tabIndex={0}
            >
              <source src={videoUrl} type={videoMimeType ?? undefined} />
            </video>
          </>
        ) : isInteractive ? (
          <button
            ref={triggerRef}
            aria-label={openDetailAriaLabel ?? imageAlt}
            className="absolute inset-0 block cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2"
            data-gallery-interactive
            onClick={onOpenDetail}
            type="button"
          >
            {imageElement}
          </button>
        ) : (
          imageElement
        )}

        {showPlayOverlay ? (
          <button
            ref={playButtonRef}
            aria-label={playAriaLabel}
            className="absolute inset-0 z-10 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2"
            data-gallery-interactive
            onClick={(event) => {
              event.stopPropagation()
              onPlayClick?.()
            }}
            type="button"
          >
            <span aria-hidden className="absolute inset-0 bg-black/50" />
            <span className="absolute left-1/2 top-1/2 inline-flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-[10px] border border-white/20 bg-bluetext/40 px-3 py-3 font-outfit text-base font-semibold text-white">
              <Play aria-hidden className="size-5 fill-turquoise text-turquoise" />
              {playLabel}
            </span>
          </button>
        ) : null}
      </div>

      <div
        aria-hidden={hasCaption ? undefined : true}
        className={cn(
          'min-h-9 p-2 font-outfit text-base leading-5 text-dark-800/95',
          captionAlignClasses[captionAlign],
        )}
      >
        {caption ? <p>{caption}</p> : null}
        {captionLine1 ? (
          <>
            <p>{captionLine1}</p>
            {captionLine2 ? <p>{captionLine2}</p> : null}
          </>
        ) : null}
      </div>
    </article>
  )
}
